import {
  ApiAuditLog,
  ApiBranch,
  ApiEmployee,
  ApiEmployeeAbsence,
  ApiHolidayEvent,
  ApiJobRole,
  ApiJobRoleWrite,
  ApiScaleRule,
  ApiScheduleShift,
  ApiTimecardPunch,
  ApiTimecardTicket,
  ApiTimecardTicketMessage,
  ApiUser,
  ApiUserWrite,
  ApiWeeklySchedule,
  ApiWorkSchedulePattern,
  ApiWorkSchedulePatternWrite,
} from '../models/api.models';
import {
  AuditLogEntry,
  BranchOperation,
  DailyOperationHours,
  Employee,
  EmployeeWeeklySchedule,
  HolidayEvent,
  JourneyRule,
  RoleMapping,
  ScheduleRuleTemplate,
  ShiftBadgeType,
  ShiftCell,
  SystemUserAccount,
  TimecardAdherenceRecord,
  TimecardCorrectionTicket,
  TimecardTicketMessage,
  VacationRecord,
} from '../models/workforce.model';

/** One day of the selected week, as shown in the schedule grid. */
export interface WeekDay {
  index: number;
  label: string;
  fullLabel?: string;
  /** dd/MM */
  dateStr: string;
  /** yyyy-MM-dd */
  iso: string;
  holiday: boolean;
}

const DAY_LABELS = ['DOM', 'SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SÁB'];
const DAY_FULL_LABELS = [
  'Domingo',
  'Segunda-feira',
  'Terça-feira',
  'Quarta-feira',
  'Quinta-feira',
  'Sexta-feira',
  'Sábado',
];
const OPERATING_DAYS: DailyOperationHours['diaSemana'][] = ['DOM', 'SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SAB'];

/** Sunday of the week that contains the given date, as yyyy-MM-dd (the schedule weeks start on Sunday). */
export function sundayOf(date: Date): string {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  d.setDate(d.getDate() - d.getDay());
  return toIsoDate(d);
}

export function toIsoDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function addDays(iso: string, days: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  return toIsoDate(new Date(y, m - 1, d + days));
}

/** True when the holiday falls on the date; fixed holidays repeat every year on the same day and month. */
export function isHolidayOn(holiday: HolidayEvent, iso: string): boolean {
  return holiday.recorrenteFixo ? holiday.data.slice(5) === iso.slice(5) : holiday.data === iso;
}

export function buildWeekDays(weekStartIso: string, holidays: HolidayEvent[]): WeekDay[] {
  return DAY_LABELS.map((label, index) => {
    const iso = addDays(weekStartIso, index);
    return {
      index,
      label,
      fullLabel: DAY_FULL_LABELS[index],
      iso,
      dateStr: `${iso.slice(8, 10)}/${iso.slice(5, 7)}`,
      holiday: holidays.some((h) => isHolidayOn(h, iso)),
    };
  });
}

// ── Branches & rules ─────────────────────────────────────────────────────────

function resolveBranchDisplayName(b: ApiBranch): string {
  const code = (b.branchCode || '').trim().toUpperCase();
  const protheus = (b.protheusCode || '').trim().toUpperCase();
  const candidates = [b.biName, b.name, b.nickName, b.protheusName];
  for (const raw of candidates) {
    const trimmed = (raw || '').trim();
    if (trimmed && trimmed.toUpperCase() !== code && trimmed.toUpperCase() !== protheus) {
      const cleaned = code
        ? trimmed
            .replace(new RegExp(`^${code}\\s*\\p{Dash_Punctuation}\\s*`, 'iu'), '')
            .replace(new RegExp(`\\s*\\p{Dash_Punctuation}\\s*${code}$`, 'iu'), '')
            .trim()
        : trimmed;
      if (cleaned) return cleaned;
    }
  }
  const brand =
    b.storeBrandName ||
    (b.segment === 'CTN'
      ? 'Madero Container'
      : b.segment === 'STH'
        ? 'Madero Steakhouse'
        : b.segment === 'JER'
          ? 'Jeronimo'
          : b.segment || 'Madero');
  const city = (b.city || '').trim();
  return city ? `${brand} - ${city}` : b.biName || b.name || b.branchCode;
}

export function toBranchOperation(b: ApiBranch, rules: ScheduleRuleTemplate[]): BranchOperation {
  const open = b.defaultOpenTime?.slice(0, 5) || '11:30';
  const close = b.defaultCloseTime?.slice(0, 5) || '23:00';
  const rule =
    rules.find((r) => r.id === String(b.scaleRuleId)) ??
    rules.find((r) => r.isDefault) ??
    rules[0];
  const displayName = resolveBranchDisplayName(b);

  const resolvedOpeningPrepHours = b.openingPrepHours ?? rule?.aberturaCozinhaHoras ?? 2;
  const resolvedClosingPrepHours = b.closingPrepHours ?? rule?.fechamentoCozinhaHoras ?? 2;
  const resolvedOpeningPrepFloorHours =
    b.openingPrepFloorHours ?? rule?.aberturaSalaoHoras ?? resolvedOpeningPrepHours;
  const resolvedClosingPrepFloorHours =
    b.closingPrepFloorHours ?? rule?.fechamentoSalaoHoras ?? resolvedClosingPrepHours;

  const horariosPorDia: DailyOperationHours[] = OPERATING_DAYS.map((diaSemana, index) => {
    const match = b.operatingHours?.find((h) => h.dayOfWeek === index);
    return {
      diaSemana,
      abertura: match?.openTime?.slice(0, 5) || open,
      fechamento: match?.closeTime?.slice(0, 5) || close,
      fechado: Boolean(match?.isClosed),
      openingPrepFloorHours: match?.openingPrepFloorHours ?? resolvedOpeningPrepFloorHours,
      closingPrepFloorHours: match?.closingPrepFloorHours ?? resolvedClosingPrepFloorHours,
    };
  });

  return {
    id: String(b.id),
    codFilial: b.branchCode,
    codProtheus: b.protheusCode ?? '',
    nomeFilialBi: displayName,
    nomeFilialProtheus: b.protheusName || displayName,
    cnpj: b.cnpj,
    emailContato: b.email ?? '',
    telefoneContato: b.contactPhone ?? '',
    marca: b.storeBrandName,
    gmt: b.gmt == null ? 'UTC-03:00' : `UTC${b.gmt >= 0 ? '+' : '-'}${String(Math.abs(b.gmt)).padStart(2, '0')}:00`,
    cidade: b.city ?? '',
    uf: b.state ?? '',
    pais: b.country ?? 'Brasil',
    empresaGrupo: b.companyGroup ?? '',
    segmento: (b.segment ?? 'CTN') as BranchOperation['segmento'],
    codDiretor: b.directorCode ?? '',
    nomeDiretor: b.directorName ?? '',
    codGoOp: b.regionalManagerCode ?? '',
    nomeGoOp: b.regionalManagerName ?? '',
    horarioGeral: `${open} - ${close}`,
    horarioAbertura: open,
    horarioFechamento: close,
    openingPrepHours: resolvedOpeningPrepHours,
    closingPrepHours: resolvedClosingPrepHours,
    openingPrepFloorHours: resolvedOpeningPrepFloorHours,
    closingPrepFloorHours: resolvedClosingPrepFloorHours,
    regraEscalaId: b.scaleRuleId != null ? String(b.scaleRuleId) : (rule?.id ?? ''),
    regraEscalaNome: rule?.nomeRegra ?? 'PADRÃO GERAL',
    status: b.isActive && b.active ? 'ATIVA' : 'INATIVA',
    horariosPorDia,
    coberturaSemanal: [96, 97, 96, 98, 95, 96, 95],
    escalaPublicada: false,
    eficienciaMedia: 96.3,
    quadroAtual: 18,
    quadroIdeal: 18,
  };
}

export function toBranchPayload(branch: BranchOperation): Partial<ApiBranch> {
  const [open, close] = (branch.horarioGeral || '').split(/\s*(?:às|-|a)\s*/i);
  const gmtMatch = /([+-]?\d{1,2})/.exec(branch.gmt ?? '');
  const parsedGmt = gmtMatch ? Number(gmtMatch[1]) : -3;
  return {
    branchCode: branch.codFilial,
    protheusCode: branch.codProtheus,
    name: branch.nomeFilialBi || branch.codFilial,
    nickName: branch.nomeFilialBi || branch.codFilial,
    biName: branch.nomeFilialBi || branch.codFilial,
    protheusName: branch.nomeFilialProtheus || branch.nomeFilialBi || branch.codFilial,
    cnpj: branch.cnpj,
    email: branch.emailContato,
    contactPhone: branch.telefoneContato,
    storeBrandName: branch.marca,
    gmt: Number.isFinite(parsedGmt) ? parsedGmt : -3,
    city: branch.cidade,
    state: branch.uf,
    country: branch.pais,
    companyGroup: branch.empresaGrupo,
    segment: branch.segmento,
    directorCode: branch.codDiretor,
    directorName: branch.nomeDiretor,
    regionalManagerCode: branch.codGoOp,
    regionalManagerName: branch.nomeGoOp,
    defaultOpenTime: branch.horarioAbertura || open || '11:30',
    defaultCloseTime: branch.horarioFechamento || close || '23:00',
    openingPrepHours: branch.openingPrepHours ?? null,
    closingPrepHours: branch.closingPrepHours ?? null,
    openingPrepFloorHours: branch.openingPrepFloorHours ?? branch.openingPrepHours ?? null,
    closingPrepFloorHours: branch.closingPrepFloorHours ?? branch.closingPrepHours ?? null,
    scaleRuleId: Number(branch.regraEscalaId) || null,
    isActive: branch.status === 'ATIVA',
    active: branch.status === 'ATIVA',
    operatingHours: (branch.horariosPorDia ?? []).map((dh, index) => {
      const dayIdx = OPERATING_DAYS.indexOf(dh.diaSemana);
      return {
        dayOfWeek: dayIdx >= 0 ? dayIdx : index,
        openTime: dh.abertura || open || '11:30',
        closeTime: dh.fechamento || close || '23:00',
        isClosed: Boolean(dh.fechado),
        openingPrepFloorHours:
          dh.openingPrepFloorHours ?? branch.openingPrepFloorHours ?? branch.openingPrepHours ?? null,
        closingPrepFloorHours:
          dh.closingPrepFloorHours ?? branch.closingPrepFloorHours ?? branch.closingPrepHours ?? null,
      };
    }),
  };
}

export function toScheduleRule(r: ApiScaleRule): ScheduleRuleTemplate {
  return {
    id: String(r.id),
    isDefault: r.isDefault,
    nomeRegra: r.name,
    segmentoAlvo: r.segment,
    maxDiasConsecutivos: r.maxConsecutiveDays,
    maxHorasSemIntervalo: r.maxConsecutiveHoursNoInterval,
    intervaloMinHoras: r.minIntervalHours,
    intervaloMaxHoras: r.maxIntervalHours,
    descansoInterjornadaHoras: r.minRestBetweenShiftsHours,
    cargaSemanalHoras: r.weeklyHoursLimit,
    cargaComFolgaDomHoras: r.sundayOffWeeklyHours,
    domingosFolgaPadraoMes: r.defaultMonthlySundaysOff ?? 1,
    domingosFolgaMulheresMes: r.femaleMonthlySundaysOff ?? 2,
    aberturaCozinhaHoras: r.openingPrepKitchenHours,
    aberturaSalaoHoras: r.openingPrepFloorHours,
    fechamentoCozinhaHoras: r.closingPrepKitchenHours,
    fechamentoSalaoHoras: r.closingPrepFloorHours,
    liderancaMinimaPresencial: r.minLeadershipCount,
    qtdAberturaCozinhaSth: r.minPrepKitchenCount,
    qtdAberturaSalaoSth: r.minPrepFloorCount,
    qtdAberturaAuxiliarCtnJer: r.minPrepAuxiliaryCount,
    evitarEntradaSaidaPico: r.avoidEntryExitPeak,
    coberturaOperacionalMinima: r.minStaffOpen,
  };
}

// ── Employees & absences ─────────────────────────────────────────────────────

const SECTOR_BY_DEPARTMENT: Record<string, Employee['setor']> = {
  MANAGEMENT: 'Gerência',
  GERENCIA: 'Gerência',
  FLOOR: 'Salão',
  SALAO: 'Salão',
  KITCHEN: 'Cozinha',
  COZINHA: 'Cozinha',
  BAR: 'Bar',
  LIMPEZA: 'Limpeza',
  CLEANING: 'Limpeza',
  SUPPORT: 'Apoio Operacional',
  APOIO_OPERACIONAL: 'Apoio Operacional',
};

function inferRoleMetadataFromName(roleName: string): {
  categoriaFuncao: Employee['categoriaFuncao'];
  contabilizaProducaoPedidos: boolean;
  defaultSector?: Employee['setor'];
} {
  const upper = (roleName || '').toUpperCase();
  if (upper.includes('VIGIA') || upper.includes('SEGURANCA') || upper.includes('SEGURANÇA')) {
    return {
      categoriaFuncao: 'FORA_OPERACAO',
      contabilizaProducaoPedidos: false,
      defaultSector: 'Apoio Operacional',
    };
  }
  if (
    upper.includes('SERVICOS GERAIS') ||
    upper.includes('SERVIÇOS GERAIS') ||
    upper.includes('ASG') ||
    upper.includes('LIMPEZA') ||
    upper.includes('FAXIN')
  ) {
    return {
      categoriaFuncao: 'LIMPEZA_APOIO',
      contabilizaProducaoPedidos: false,
      defaultSector: 'Limpeza',
    };
  }
  return {
    categoriaFuncao: 'PRODUCAO',
    contabilizaProducaoPedidos: true,
  };
}

export function toHierarchyLevel(level?: string | null): 'BASE' | 'LIDERANCA' {
  const upper = (level ?? '').trim().toUpperCase();
  if (
    upper === 'LEADERSHIP' ||
    upper === 'LIDERANCA' ||
    upper === 'GERENTE_OPERACAO' ||
    upper === 'COORDENADOR_OPERACAO'
  ) {
    return 'LIDERANCA';
  }
  return 'BASE';
}

export function toSector(department?: string | null, roleName?: string | null): Employee['setor'] {
  const inferredMeta = inferRoleMetadataFromName(roleName || '');
  if (inferredMeta.defaultSector) {
    return inferredMeta.defaultSector;
  }
  const deptUpper = (department ?? '').trim().toUpperCase();
  if (deptUpper && deptUpper !== 'AMBOS' && SECTOR_BY_DEPARTMENT[deptUpper]) {
    return SECTOR_BY_DEPARTMENT[deptUpper];
  }
  const upperRole = (roleName ?? '').trim().toUpperCase();
  if (upperRole.includes('COZINH') || upperRole.includes('CHAPEIR')) {
    return 'Cozinha';
  }
  if (upperRole.includes('GERENT') || upperRole.includes('COORDENAD')) {
    return 'Gerência';
  }
  if (
    upperRole.includes('LIMPEZ') ||
    upperRole.includes('SERVICOS GERAIS') ||
    upperRole.includes('SERVIÇOS GERAIS') ||
    upperRole.includes('ASG') ||
    upperRole.includes('FAXIN')
  ) {
    return 'Limpeza';
  }
  if (upperRole.includes('VIGIA') || upperRole.includes('SEGURANCA') || upperRole.includes('SEGURANÇA')) {
    return 'Apoio Operacional';
  }
  return SECTOR_BY_DEPARTMENT[deptUpper] ?? 'Salão';
}

export function toEmployeeStatus(apiStatus: string): Employee['situacaoReal'] {
  switch ((apiStatus || '').toUpperCase()) {
    case 'VACATION':
    case 'FERIAS':
      return 'FERIAS';
    case 'MEDICALLEAVE':
    case 'MEDICAL_LEAVE':
    case 'AFASTADO':
    case 'INACTIVE':
      return 'AFASTADO';
    case 'NOTICE':
    case 'AVISO_PREVIO':
      return 'AVISO_PREVIO';
    default:
      return 'ATIVO';
  }
}

export function fromEmployeeStatus(status: Employee['situacaoReal']): string {
  switch (status) {
    case 'FERIAS':
      return 'Vacation';
    case 'AFASTADO':
      return 'MedicalLeave';
    case 'AVISO_PREVIO':
      return 'Notice';
    default:
      return 'Active';
  }
}

export function toEmployee(
  e: ApiEmployee,
  jobRoles: Map<number, ApiJobRole>,
  branch?: BranchOperation,
  patterns?: Map<number, ApiWorkSchedulePattern>
): Employee {
  const role = e.jobRoleId != null ? jobRoles.get(e.jobRoleId) : undefined;
  const pattern = e.workSchedulePatternId != null ? patterns?.get(e.workSchedulePatternId) : undefined;
  const resolvedRoleName = role?.standardName || e.rawRoleName || '';
  const inferredMeta = inferRoleMetadataFromName(resolvedRoleName);
  const categoriaFuncao = role ? toCategoriaFuncao(role) : inferredMeta.categoriaFuncao || 'PRODUCAO';
  const contabilizaProducaoPedidos = role ? countsInProduction(role) : inferredMeta.contabilizaProducaoPedidos;
  const primarySector = toSector(role?.department, resolvedRoleName);
  const parsedTags = (e.sectorTags || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  const resolvedTags = parsedTags.length > 0 ? parsedTags : [primarySector];
  const generoUpper = (e.gender || '').trim().toUpperCase();

  return {
    id: String(e.id),
    chapa: e.employeeCode,
    cpf: e.cpf || '',
    genero: generoUpper.startsWith('F') ? 'F' : 'M',
    nome: e.fullName,
    funcao: resolvedRoleName,
    funcaoCod:
      e.rawRoleCode ||
      role?.rmRoles?.[0]?.roleCode ||
      role?.internalCode ||
      role?.commercialGroup ||
      'RM',
    cargoNivel: toHierarchyLevel(role?.hierarchyLevel),
    setor: primarySector,
    setorTags: resolvedTags,
    setoresTags: resolvedTags,
    categoriaFuncao,
    contabilizaProducaoPedidos,
    situacaoReal: toEmployeeStatus(e.status),
    dataAdmissao: e.admissionDate ? e.admissionDate.slice(0, 10) : '',
    codFilial: branch?.codFilial ?? '',
    nomeFilial: branch?.nomeFilialBi ?? '',
    regraJornadaId: e.workSchedulePatternId == null ? '' : String(e.workSchedulePatternId),
    regraJornadaNome: pattern?.name || e.rawScheduleName || '',
    horasContratuaisSemanais: e.contractWeeklyHours ?? undefined,
    jornadaDiariaMinutos: e.customDailyWorkloadMinutes ?? undefined,
    domingosTrabalhadosConsecutivos: 0,
    afastamentos: (e.absences ?? []).map((a) => ({
      id: String(a.id),
      tipo: toVacationType(a.absenceType),
      dataInicio: a.startDate.slice(0, 10),
      dataFim: a.endDate.slice(0, 10),
      motivo: a.observation ?? '',
      registradoPor: '',
      registradoEm: '',
    })),
  };
}

/** Keeps every field the screen does not edit, so an update does not wipe them in the API. */
export function toEmployeePayload(emp: Employee, original: ApiEmployee | undefined, branchId: number): Partial<ApiEmployee> {
  const tags =
    emp.setorTags && emp.setorTags.length > 0
      ? emp.setorTags
      : emp.setoresTags && emp.setoresTags.length > 0
        ? emp.setoresTags
        : undefined;
  return {
    ...original,
    branchId,
    employeeCode: emp.chapa,
    fullName: emp.nome,
    cpf: emp.cpf || original?.cpf,
    gender: emp.genero || original?.gender,
    contractWeeklyHours: emp.horasContratuaisSemanais ?? original?.contractWeeklyHours ?? null,
    customDailyWorkloadMinutes: emp.jornadaDiariaMinutos ?? original?.customDailyWorkloadMinutes ?? null,
    sectorTags: tags && tags.length > 0 ? tags.join(',') : (original?.sectorTags ?? emp.setor),
    status: fromEmployeeStatus(emp.situacaoReal),
    admissionDate: emp.dataAdmissao || original?.admissionDate || null,
    rawRoleName: original?.rawRoleName ?? emp.funcao,
    workSchedulePatternId: toPositiveInt(emp.regraJornadaId) ?? original?.workSchedulePatternId ?? null,
    rawScheduleName: emp.regraJornadaNome || original?.rawScheduleName,
    absences: undefined,
  };
}

function toPositiveInt(value: string | number | null | undefined): number | undefined {
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : undefined;
}

// ── Job roles (Funções do RM) ───────────────────────────────────────────────

const PRODUCTION_CATEGORY = 'PRODUCAO';

/** True when the role enters the order production curve; follows the API flag, then the category. */
export function countsInProduction(role: Pick<ApiJobRole, 'countsInOrderProduction' | 'roleCategory'>): boolean {
  if (role.countsInOrderProduction !== undefined && role.countsInOrderProduction !== null) {
    return role.countsInOrderProduction;
  }
  const category = (role.roleCategory || PRODUCTION_CATEGORY).toUpperCase();
  return category === PRODUCTION_CATEGORY;
}

/** Translates the API roleCategory (current and legacy values) into the employee category used by the engine. */
export function toCategoriaFuncao(
  role: Pick<ApiJobRole, 'countsInOrderProduction' | 'roleCategory' | 'standardName'>
): NonNullable<Employee['categoriaFuncao']> {
  const category = (role.roleCategory || '').trim().toUpperCase();
  switch (category) {
    case 'PRODUCAO':
      return 'PRODUCAO';
    case 'LIMPEZA_PICO':
    case 'LIMPEZA_APOIO':
    case 'APOIO_PICO':
      return 'LIMPEZA_APOIO';
    case 'APOIO_SEM_PRODUCAO':
    case 'FORA_OPERACAO':
      return 'FORA_OPERACAO';
    default: {
      if (countsInProduction(role)) return 'PRODUCAO';
      const inferred = inferRoleMetadataFromName(role.standardName).categoriaFuncao;
      return inferred === 'LIMPEZA_APOIO' ? 'LIMPEZA_APOIO' : 'FORA_OPERACAO';
    }
  }
}

export function toRoleMapping(r: ApiJobRole): RoleMapping {
  return {
    id: String(r.id),
    codigoInterno: (r.internalCode ?? '').trim(),
    nomePadraoFuncao: r.standardName,
    nomeComercialGrupo: r.commercialGroup ?? '',
    nivelAtuacao: toHierarchyLevel(r.hierarchyLevel),
    departamento: r.department ?? '',
    descricao: r.description ?? '',
    contabilizaProducaoPedidos: countsInProduction(r),
    modoAlocacao: (r.roleCategory || PRODUCTION_CATEGORY).trim().toUpperCase(),
    funcoesRm: (r.rmRoles ?? []).map((rm) => ({
      coligada: rm.companyCode ?? '',
      codigo: rm.roleCode ?? '',
      nome: rm.roleName ?? '',
    })),
    nomesOrigem: r.mappedSourceRoleNames ?? [],
    totalColaboradores: r.employeeCount ?? 0,
  };
}

/** Body of PUT/POST job-roles: keeps every field the screen does not edit. */
export function toJobRoleWrite(role: RoleMapping, original?: ApiJobRole): ApiJobRoleWrite {
  return {
    standardName: role.nomePadraoFuncao.trim(),
    internalCode: role.codigoInterno.trim() || null,
    commercialGroup: role.nomeComercialGrupo || original?.commercialGroup,
    hierarchyLevel: role.nivelAtuacao === 'LIDERANCA' ? 'Leadership' : 'Base',
    department: role.departamento || original?.department,
    description: role.descricao.trim() || null,
    countsInOrderProduction: role.contabilizaProducaoPedidos,
    roleCategory: role.modoAlocacao || PRODUCTION_CATEGORY,
    mappedSourceRoleNames: original?.mappedSourceRoleNames ?? role.nomesOrigem,
    rmRoles: original?.rmRoles ?? [],
  };
}

// ── Work schedule patterns (Jornadas) ───────────────────────────────────────

/** 440 -> "07:20". */
export function formatMinutesAsTime(totalMinutes: number): string {
  const safe = Math.max(0, Math.round(totalMinutes || 0));
  return `${String(Math.floor(safe / 60)).padStart(2, '0')}:${String(safe % 60).padStart(2, '0')}`;
}

/** "07:20" -> 440; null when the text is not a valid HH:mm. */
export function parseTimeToMinutes(value: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec((value ?? '').trim());
  if (!m || Number(m[2]) > 59) return null;
  return Number(m[1]) * 60 + Number(m[2]);
}

export function toJourneyRule(p: ApiWorkSchedulePattern): JourneyRule {
  const minutes = p.dailyWorkloadMinutes ?? 0;
  return {
    id: String(p.id),
    nomeParametrizacao: p.name,
    diasTrabalhoSemana: p.workDaysPerWeek,
    folgasSemana: p.offDaysPerWeek,
    jornadaDiariaMinutos: minutes,
    jornadaDiaria: p.dailyWorkloadFormatted || formatMinutesAsTime(minutes),
    cargaSemanalHoras: Math.round(((p.workDaysPerWeek * minutes) / 60) * 10) / 10,
    folgaExtraMensal: p.hasExtraMonthlySundayOff,
    diaFolgaExtra: p.extraOffDayOfWeek ?? null,
    descricao: p.description ?? '',
  };
}

export function toWorkSchedulePatternWrite(rule: JourneyRule): ApiWorkSchedulePatternWrite {
  return {
    name: rule.nomeParametrizacao.trim(),
    workDaysPerWeek: rule.diasTrabalhoSemana,
    offDaysPerWeek: rule.folgasSemana,
    dailyWorkloadMinutes: rule.jornadaDiariaMinutos,
    dailyWorkloadFormatted: formatMinutesAsTime(rule.jornadaDiariaMinutos),
    hasExtraMonthlySundayOff: rule.folgaExtraMensal,
    extraOffDayOfWeek: rule.diaFolgaExtra,
    description: rule.descricao.trim() || null,
  };
}

// ── Timecard punches (Aderência de ponto) ───────────────────────────────────

type DeviationStage = NonNullable<TimecardAdherenceRecord['etapaDesvio']>;

const DEVIATION_STAGES: DeviationStage[] = [
  'NORMAL',
  'DESVIO_ENTRADA',
  'DESVIO_INTERVALO',
  'DESVIO_SAIDA',
  'AUSENCIA',
  'FERIAS_AFASTAMENTO',
];

const PAYCODE_DESCRIPTION_BY_STAGE: Record<DeviationStage, string> = {
  NORMAL: 'Horas normais',
  DESVIO_ENTRADA: 'Desvio de entrada',
  DESVIO_INTERVALO: 'Desvio de intervalo',
  DESVIO_SAIDA: 'Desvio de saída',
  AUSENCIA: 'Ausência',
  FERIAS_AFASTAMENTO: 'Férias ou afastamento',
};

/** "09:30:00" or "09:30" -> "09:30"; empty when there is no punch. */
function toPunchTime(value?: string | null): string {
  const m = /(\d{1,2}):(\d{2})/.exec(value ?? '');
  return m ? `${m[1].padStart(2, '0')}:${m[2]}` : '';
}

export function toDeviationStage(p: Pick<ApiTimecardPunch, 'deviationStage' | 'adherenceStatus' | 'absenceMinutes'>): DeviationStage {
  const rawStage = (p.deviationStage ?? '').trim().toUpperCase();
  if (rawStage === 'TRABALHO_EM_FOLGA' || rawStage === 'SEM_ESCALA') return 'DESVIO_SAIDA';
  const stage = rawStage as DeviationStage;
  if (DEVIATION_STAGES.includes(stage)) return stage;
  const status = (p.adherenceStatus ?? '').trim().toUpperCase();
  if (status === 'EXCUSED') return 'FERIAS_AFASTAMENTO';
  if (status === 'ABSENCE' || (p.absenceMinutes ?? 0) > 0) return 'AUSENCIA';
  return 'NORMAL';
}

function toAdherenceStatus(stage: DeviationStage, p: ApiTimecardPunch): TimecardAdherenceRecord['statusAderencia'] {
  switch (stage) {
    case 'FERIAS_AFASTAMENTO':
      return 'FERIAS';
    case 'AUSENCIA':
      return 'FALTA';
    case 'DESVIO_SAIDA':
      return (p.overtimeMinutes ?? 0) > 0 ? 'HORA_EXTRA' : 'ATRASO';
    case 'DESVIO_ENTRADA':
    case 'DESVIO_INTERVALO':
      return (p.adherenceStatus ?? '').toUpperCase() === 'OVERTIME' ? 'HORA_EXTRA' : 'ATRASO';
    default:
      return 'ADERENTE';
  }
}

function toDaySituation(stage: DeviationStage): TimecardAdherenceRecord['situacaoDia'] {
  if (stage === 'FERIAS_AFASTAMENTO') return 'FERIAS';
  if (stage === 'AUSENCIA') return 'FALTA';
  return 'TRABALHADO';
}

function joinInterval(start: string, end: string): string {
  return start || end ? `${start || '--:--'} às ${end || '--:--'}` : '';
}

/** Maps one imported punch day to the row of the adherence screen (4 planned x 4 actual + deviation stage). */
export function toTimecardRecord(
  p: ApiTimecardPunch,
  employee?: Employee,
  branch?: BranchOperation
): TimecardAdherenceRecord {
  const stage = toDeviationStage(p);
  const plannedIn1 = toPunchTime(p.plannedIn1);
  const plannedOut1 = toPunchTime(p.plannedOut1);
  const plannedIn2 = toPunchTime(p.plannedIn2);
  const plannedOut2 = toPunchTime(p.plannedOut2);
  const actualIn1 = toPunchTime(p.inPunch1);
  const actualOut1 = toPunchTime(p.outPunch1);
  const actualIn2 = toPunchTime(p.inPunch2);
  const actualOut2 = toPunchTime(p.outPunch2);
  const deviation =
    stage === 'AUSENCIA'
      ? (p.absenceMinutes ?? 0)
      : Math.max(p.delayMinutes ?? 0, p.overtimeMinutes ?? 0, p.absenceMinutes ?? 0);

  return {
    id: String(p.id),
    data: (p.punchDate ?? '').slice(0, 10),
    codFilial: branch?.codFilial ?? employee?.codFilial ?? '',
    nomeFilial: p.branchName || branch?.nomeFilialBi || employee?.nomeFilial || '',
    chapa: p.employeeCode || employee?.chapa || '',
    cpf: p.employeeCpf || employee?.cpf || '',
    nomeColaborador: p.employeeName || employee?.nome || '',
    funcao: employee?.funcao ?? '',
    setor: employee?.setor ?? '',
    previstoEntrada: plannedIn1,
    previstoSaida1: plannedOut1,
    previstoEntrada2: plannedIn2,
    previstoIntervalo: joinInterval(plannedOut1, plannedIn2),
    previstoSaida: plannedOut2,
    realizadoEntrada: actualIn1,
    realizadoSaida1: actualOut1,
    realizadoEntrada2: actualIn2,
    realizadoIntervalo: joinInterval(actualOut1, actualIn2),
    realizadoSaida: actualOut2,
    desvioMinutos: deviation,
    etapaDesvio: stage,
    situacaoDia: toDaySituation(stage),
    statusAderencia: toAdherenceStatus(stage, p),
    paycodeCodigo: p.payCode ?? '',
    paycodeDescricao: PAYCODE_DESCRIPTION_BY_STAGE[stage],
  };
}

const VACATION_TYPES: VacationRecord['tipo'][] = [
  'FERIAS',
  'LICENCA_MATERNIDADE',
  'LICENCA_PATERNIDADE',
  'LICENCA_MEDICA',
  'AFASTAMENTO_INSS',
  'AVISO_PREVIO',
];

export function toVacationType(apiType: string): VacationRecord['tipo'] {
  const code = (apiType || '').toUpperCase();
  if (code === 'VACATION') return 'FERIAS';
  if (code === 'MEDICAL_LEAVE' || code === 'MEDICALLEAVE') return 'LICENCA_MEDICA';
  return VACATION_TYPES.includes(code as VacationRecord['tipo']) ? (code as VacationRecord['tipo']) : 'LICENCA_MEDICA';
}

export function fromVacationType(tipo: VacationRecord['tipo']): string {
  if (tipo === 'FERIAS') return 'VACATION';
  if (tipo === 'LICENCA_MEDICA') return 'MEDICAL_LEAVE';
  return tipo;
}

export function toVacationRecord(a: ApiEmployeeAbsence, employee?: Employee, today = toIsoDate(new Date())): VacationRecord {
  const start = a.startDate.slice(0, 10);
  const end = a.endDate.slice(0, 10);
  const rawObs = a.observation ?? '';
  const isManual = rawObs.startsWith('[MANUAL]');
  const cleanObs = isManual ? rawObs.replace(/^\[MANUAL\]\s*/, '') : rawObs;
  return {
    id: String(a.id),
    chapa: employee?.chapa ?? '',
    nomeColaborador: employee?.nome ?? '',
    funcao: employee?.funcao ?? '',
    codFilial: employee?.codFilial ?? '',
    nomeFilial: employee?.nomeFilial ?? '',
    tipo: toVacationType(a.absenceType),
    dataInicio: start,
    dataFim: end,
    diasTotais: a.daysCount,
    origem: isManual ? 'MANUAL' : 'IMPORTACAO_RM',
    status: end < today ? 'CONCLUIDO' : start <= today ? 'EM_GOZO' : 'PROGRAMADO',
    motivo: cleanObs || a.observation,
  };
}

// ── Tickets ──────────────────────────────────────────────────────────────────

export function toTicketStatus(apiStatus: string): TimecardCorrectionTicket['status'] {
  switch ((apiStatus || '').toUpperCase()) {
    case 'APPROVED':
    case 'RESOLVED':
      return 'APROVADO';
    case 'REJECTED':
      return 'RECUSADO';
    case 'IN_REVIEW':
    case 'INREVIEW':
    case 'INPROGRESS':
      return 'EM_ANALISE';
    default:
      return 'ABERTO';
  }
}

export function fromTicketStatus(status: TimecardCorrectionTicket['status']): string {
  switch (status) {
    case 'APROVADO':
      return 'APPROVED';
    case 'RECUSADO':
      return 'REJECTED';
    case 'EM_ANALISE':
      return 'IN_REVIEW';
    default:
      return 'OPEN';
  }
}

function toMessageProfile(role?: string): 'Gestor' | 'Equipe de Ponto' | 'Admin' {
  if (role === 'Admin') return 'Admin';
  if (role === 'TimecardTeam' || role === 'Equipe de Ponto') return 'Equipe de Ponto';
  return 'Gestor';
}

function formatDateTime(iso?: string): string {
  return iso ? new Date(iso).toLocaleString('pt-BR').slice(0, 17) : '';
}

export function toTicket(t: ApiTimecardTicket, branch?: BranchOperation): TimecardCorrectionTicket {
  return {
    id: String(t.id),
    ticketCode: t.ticketCode,
    dataOcorrencia: t.occurrenceDate.slice(0, 10),
    codFilial: branch?.codFilial ?? '',
    nomeFilial: branch?.nomeFilialBi ?? '',
    chapa: t.employeeCode ?? '',
    nomeColaborador: t.employeeName ?? '',
    tipoAjuste: t.reasonCategory as TimecardCorrectionTicket['tipoAjuste'],
    horarioPrevisto: '',
    horarioRealizado: '',
    horarioSolicitado: '',
    justificativaGestor: t.justification,
    solicitante: t.messages?.[0]?.senderName ?? '',
    status: toTicketStatus(t.status),
    criadoEm: formatDateTime(t.createdAt),
    mensagens: (t.messages ?? []).map(toTicketMessage),
  };
}

export function toTicketMessage(m: ApiTimecardTicketMessage, index = 0): TimecardTicketMessage {
  return {
    id: String(m.id ?? `msg-${index}`),
    autor: m.senderName ?? '',
    perfil: toMessageProfile(m.senderRole),
    texto: m.messageText,
    dataHora: formatDateTime(m.createdAt),
  };
}

// ── Holidays, users, audit ───────────────────────────────────────────────────

export function toHoliday(h: ApiHolidayEvent): HolidayEvent {
  const factor = Number(h.impactFactor ?? 1);
  const pct = Math.round((factor - 1) * 100);
  const scope = (h.scopeType || '').toUpperCase();
  const date = h.eventDate.slice(0, 10);
  return {
    id: String(h.id),
    data: date,
    nome: h.name,
    escopo:
      scope === 'STATE' || scope === 'ESTADUAL'
        ? 'ESTADUAL'
        : scope === 'CITY' || scope === 'MUNICIPAL'
          ? 'MUNICIPAL'
          : scope === 'EVENTO_ESPECIAL'
            ? 'EVENTO_ESPECIAL'
            : 'NACIONAL',
    ufMunicipio: [h.state, h.city].filter(Boolean).join(' / ') || 'Brasil',
    fatorImpactoPct: pct,
    impactFactorMultiplier: factor,
    recorrenteFixo: h.isRecurring,
    categoriaData: h.isRecurring ? 'FIXO' : 'MOVEL',
    observacao: `Multiplicador ${factor}x (${pct >= 0 ? '+' : ''}${pct}%).`,
  };
}

const PROFILE_BY_ROLE: Record<string, SystemUserAccount['perfil']> = {
  Admin: 'Admin',
  TimecardTeam: 'Equipe de Ponto',
  RegionalManager: 'Gerente Regional',
  Director: 'Diretor',
};

export function toSystemUser(u: ApiUser): SystemUserAccount {
  return {
    id: String(u.id),
    nome: u.name,
    username: u.username,
    email: u.email,
    cpf: u.cpf ?? '',
    cargo: u.role,
    perfil: PROFILE_BY_ROLE[u.role] ?? 'Gestor',
    filiaisVinculadas: u.branchCodes ?? [],
    status: u.active ? 'ATIVO' : 'BLOQUEADO',
    ultimoAcesso: '',
  };
}

export function toUserPayload(user: SystemUserAccount, branchIdsByCode: Map<string, number>): ApiUserWrite {
  const role =
    user.perfil === 'Admin'
      ? 'Admin'
      : user.perfil === 'Equipe de Ponto'
        ? 'TimecardTeam'
        : user.perfil === 'Gerente Regional'
          ? 'RegionalManager'
          : user.perfil === 'Diretor'
            ? 'Director'
            : 'StoreManager';
  return {
    name: user.nome,
    username: user.username,
    email: user.email,
    cpf: user.cpf,
    role,
    mustChangePassword: false,
    active: user.status === 'ATIVO',
    branchIds: user.filiaisVinculadas
      .map((code) => branchIdsByCode.get(code.trim()))
      .filter((id): id is number => id !== undefined),
  };
}

export function toAuditLogEntry(a: ApiAuditLog): AuditLogEntry {
  return {
    id: String(a.id),
    timestamp: new Date(a.createdAt).toLocaleString('pt-BR'),
    usuarioNome: a.userName,
    usuarioCpf: a.userCpf || '',
    usuarioRole: 'Admin',
    modulo: a.module,
    acao: a.actionType,
    colaboradorNome: a.targetEmployee || '',
    colaboradorCpf: a.targetEmployeeCpf || '',
    detalhe: a.details,
    valorAnterior: a.oldValueJson || '-',
    valorNovo: a.newValueJson || '-',
    codFilial: a.userBranch ?? '',
    revertido: a.isReverted,
    justificativa: a.justification,
  };
}

// ── Weekly schedule ──────────────────────────────────────────────────────────

const TAG_TO_TYPE: Record<string, ShiftBadgeType> = { OPENING: 'ABERTURA', CLOSING: 'FECHAMENTO' };
const TYPE_TO_TAG: Partial<Record<ShiftBadgeType, string>> = {
  ABERTURA: 'OPENING',
  FECHAMENTO: 'CLOSING',
  INTERMEDIARIO: 'MID',
};

function toShiftType(s: ApiScheduleShift): ShiftBadgeType {
  if (s.isAbsence) {
    const type = (s.absenceType ?? '').toUpperCase();
    return type === 'VACATION' || type === 'FERIAS' ? 'FERIAS' : 'AFASTADO';
  }
  if (s.isDayOff) return 'FOLGA';
  const baseTag = (s.shiftTag ?? '').split(':')[0].toUpperCase();
  return TAG_TO_TYPE[baseTag] ?? 'INTERMEDIARIO';
}

export function extractDomingoFolgaMes(shiftTag?: string | null): number | undefined {
  if (!shiftTag) return undefined;
  const match = /:DOM(\d+)/i.exec(shiftTag) || /DOM_FOLGA_(\d+)/i.exec(shiftTag);
  if (match) return Number(match[1]);
  if (shiftTag.toUpperCase().includes('DOM_SEM_FOLGA_MES')) return 0;
  return undefined;
}

export function toShiftCell(day: WeekDay, s: ApiScheduleShift | undefined): ShiftCell {
  if (!s) {
    return {
      dayIndex: day.index,
      dayLabel: day.label,
      dateStr: day.dateStr,
      dataIso: day.iso,
      tipo: 'FOLGA',
      entrada: '-',
      saidaIntervalo: '-',
      retornoIntervalo: '-',
      saida: '-',
      editadoManualmente: false,
      editadoPosPublicacao: false,
      cltAlertas: [],
    };
  }
  const tipo = toShiftType(s);
  const off = tipo === 'FOLGA';
  const absent = tipo === 'FERIAS' || tipo === 'AFASTADO';
  const domingoFolgaMes = extractDomingoFolgaMes(s.shiftTag);
  const rawWarn = s.cltWarningDetails || s.cltWarningMessage;
  const cltAlertas =
    s.hasCltWarning && rawWarn ? rawWarn.split(/\s*\|\s*/).filter(Boolean) : [];
  return {
    dayIndex: day.index,
    dayLabel: day.label,
    dateStr: day.dateStr,
    dataIso: s.shiftDate ? s.shiftDate.slice(0, 10) : day.iso,
    tipo,
    entrada: off ? 'FOLGA' : absent ? tipo : (s.startTime ?? '-'),
    saidaIntervalo: off || absent ? '-' : (s.breakStartTime ?? '-'),
    retornoIntervalo: off || absent ? '-' : (s.breakEndTime ?? '-'),
    saida: off ? 'FOLGA' : absent ? '-' : (s.endTime ?? '-'),
    editadoManualmente: s.isManualEdit,
    editadoPosPublicacao: Boolean(s.isPostPublicationEdit),
    justificativa: s.justification ?? undefined,
    cltAlertas,
    shiftId: s.id,
    updatedAt: s.updatedAt,
    workedMinutes: s.workedMinutes,
    ...(domingoFolgaMes !== undefined ? { domingoFolgaMes } : {}),
  };
}

const toCell = toShiftCell;

export function resolveEffectiveWeeklyHours(
  modeloEscala: string | undefined,
  baseHours: number,
  shiftsOrFolgasCount: Pick<ShiftCell, 'dayIndex' | 'tipo'>[] | number,
  apiEffectiveHours?: number,
  sundayOffWeeklyHours = 36,
  hasSundayOffParam?: boolean
): number {
  if (apiEffectiveHours != null && apiEffectiveHours > 0) {
    return apiEffectiveHours;
  }
  if ((modeloEscala || '6X1').toUpperCase() === '6X1') {
    const folgasCount =
      typeof shiftsOrFolgasCount === 'number'
        ? shiftsOrFolgasCount
        : shiftsOrFolgasCount.filter(
            (c) => c.tipo === 'FOLGA' || c.tipo === 'FERIAS' || c.tipo === 'AFASTADO'
          ).length;
    const hasSundayOff =
      typeof shiftsOrFolgasCount === 'number'
        ? (hasSundayOffParam ?? true)
        : shiftsOrFolgasCount.some(
            (s) =>
              s.dayIndex === 0 &&
              (s.tipo === 'FOLGA' || s.tipo === 'FERIAS' || s.tipo === 'AFASTADO')
          );
    if (folgasCount >= 2 && hasSundayOff) {
      return sundayOffWeeklyHours;
    }
    if (baseHours === sundayOffWeeklyHours) {
      return 44;
    }
  }
  return baseHours;
}

export function resolveScaleDefaults(
  regraJornadaNome: string,
  customHours?: number,
  pattern?: Pick<ApiWorkSchedulePattern, 'name' | 'workDaysPerWeek' | 'offDaysPerWeek' | 'dailyWorkloadMinutes'>,
  customDailyMinutes?: number
): {
  modeloEscala: string;
  folgasSemanaisPadrao: number;
  folgasSemanaisObrigatorias: number;
  horasContratuais: number;
} {
  if (pattern && pattern.workDaysPerWeek > 0) {
    const dailyMin =
      customDailyMinutes && customDailyMinutes > 0
        ? customDailyMinutes
        : pattern.dailyWorkloadMinutes;
    const patternHours = Math.round(((pattern.workDaysPerWeek * dailyMin) / 60) * 10) / 10;
    const matchRatio = /(\d+\s*X\s*\d+)/i.exec(pattern.name || regraJornadaNome || '');
    const modeloEscala = matchRatio
      ? matchRatio[1].replace(/\s+/g, '').toUpperCase()
      : `${pattern.workDaysPerWeek}X${pattern.offDaysPerWeek}`;
    return {
      modeloEscala,
      folgasSemanaisPadrao: pattern.offDaysPerWeek,
      folgasSemanaisObrigatorias: pattern.offDaysPerWeek,
      horasContratuais: customHours && customHours > 0 ? customHours : patternHours,
    };
  }
  const upper = (regraJornadaNome || '').toUpperCase();
  let modeloEscala = '6X1';
  let workDays = 6;
  let folgasSemanaisPadrao = 1;
  let defaultHours = 44;
  if (upper.includes('12X36')) {
    modeloEscala = '12X36';
    workDays = 3;
    folgasSemanaisPadrao = 3;
    defaultHours = 36;
  } else if (upper.includes('5X2')) {
    modeloEscala = '5X2';
    workDays = 5;
    folgasSemanaisPadrao = 2;
    defaultHours = 44;
  } else if (upper.includes('4X3')) {
    modeloEscala = '4X3';
    workDays = 4;
    folgasSemanaisPadrao = 3;
    defaultHours = 29.3;
  } else if (upper.includes('3X4')) {
    modeloEscala = '3X4';
    workDays = 3;
    folgasSemanaisPadrao = 4;
    defaultHours = 22;
  } else {
    const customMatch = /(\d+)\s*X\s*(\d+)/.exec(upper);
    if (customMatch) {
      workDays = Number(customMatch[1]);
      folgasSemanaisPadrao = Number(customMatch[2]);
      modeloEscala = `${workDays}X${folgasSemanaisPadrao}`;
      defaultHours = Math.round(((workDays * (customDailyMinutes || 440)) / 60) * 10) / 10;
    }
  }
  const calculatedFromDaily =
    customDailyMinutes && customDailyMinutes > 0
      ? Math.round(((workDays * customDailyMinutes) / 60) * 10) / 10
      : defaultHours;
  return {
    modeloEscala,
    folgasSemanaisPadrao,
    folgasSemanaisObrigatorias: folgasSemanaisPadrao,
    horasContratuais: customHours && customHours > 0 ? customHours : calculatedFromDaily,
  };
}

export function toWeekRows(
  schedule: ApiWeeklySchedule,
  employees: Employee[],
  weekDays: WeekDay[],
  patterns?: Map<number, ApiWorkSchedulePattern>
): EmployeeWeeklySchedule[] {
  const shiftsByEmployee = new Map<number, ApiScheduleShift[]>();
  for (const s of schedule.shifts) {
    const list = shiftsByEmployee.get(s.employeeId) ?? [];
    list.push(s);
    shiftsByEmployee.set(s.employeeId, list);
  }

  return employees
    .filter((emp) => shiftsByEmployee.has(Number(emp.id)))
    .map((emp) => {
      const shifts = shiftsByEmployee.get(Number(emp.id)) ?? [];
      const cells = weekDays.map((day) => toCell(day, shifts.find((s) => s.dayOfWeekIndex === day.index)));
      const minutes = shifts.reduce((acc, s) => acc + (s.workedMinutes || 0), 0);
      const patternId = Number(emp.regraJornadaId);
      const pattern =
        Number.isInteger(patternId) && patternId > 0 ? patterns?.get(patternId) : undefined;
      const { modeloEscala, folgasSemanaisPadrao, folgasSemanaisObrigatorias, horasContratuais } =
        resolveScaleDefaults(
          emp.regraJornadaNome,
          emp.horasContratuaisSemanais,
          pattern,
          emp.jornadaDiariaMinutos
        );
      const firstShiftWithMeta =
        shifts.find(
          (s) =>
            s.monthlyOffDaysCount != null ||
            s.monthlyOffDaysTarget != null ||
            s.monthlySundayOffCount != null ||
            s.monthlySundayOffTarget != null ||
            s.effectiveWeeklyHoursLimit != null
        ) ?? shifts[0];
      const shiftsForCount =
        shifts.length >= 7
          ? cells
          : shifts.map((s) => ({ dayIndex: s.dayOfWeekIndex, tipo: toShiftType(s) }));
      const folgasSemana = shiftsForCount.filter((c) => c.tipo === 'FOLGA').length;
      const domFolgaSemana = shiftsForCount.some((c) => c.dayIndex === 0 && c.tipo === 'FOLGA')
        ? 1
        : 0;
      const isFemale = (emp.genero || '').toUpperCase().startsWith('F');
      const folgasMesPrevistas =
        firstShiftWithMeta?.monthlyOffDaysTarget ??
        (modeloEscala === '6X1' ? (isFemale ? 6 : 5) : folgasSemanaisPadrao * 4);
      const folgasMesAtual = firstShiftWithMeta?.monthlyOffDaysCount ?? folgasSemana;
      const domingosFolgaMesPrevistos =
        firstShiftWithMeta?.monthlySundayOffTarget ?? (isFemale ? 2 : 1);
      const domingosFolgaMesAtual =
        firstShiftWithMeta?.monthlySundayOffCount ?? domFolgaSemana;
      const effectiveHorasContratuais = resolveEffectiveWeeklyHours(
        modeloEscala,
        horasContratuais,
        shiftsForCount,
        firstShiftWithMeta?.effectiveWeeklyHoursLimit
      );
      const resolvedTags =
        emp.setorTags && emp.setorTags.length > 0
          ? emp.setorTags
          : emp.setoresTags && emp.setoresTags.length > 0
            ? emp.setoresTags
            : [emp.setor];
      return {
        employeeId: emp.id,
        chapa: emp.chapa,
        cpf: emp.cpf,
        genero: emp.genero,
        nome: emp.nome,
        funcao: emp.funcao,
        cargoNivel: emp.cargoNivel,
        setor: emp.setor,
        setorTags: resolvedTags,
        setoresTags: resolvedTags,
        categoriaFuncao: emp.categoriaFuncao,
        contabilizaProducaoPedidos: emp.contabilizaProducaoPedidos,
        situacaoReal: emp.situacaoReal,
        codFilial: emp.codFilial,
        nomeFilial: emp.nomeFilial,
        regraJornadaNome: emp.regraJornadaNome,
        modeloEscala,
        folgasSemanaisPadrao,
        folgasSemanaisObrigatorias,
        jornadaDiariaMinutos: emp.jornadaDiariaMinutos ?? pattern?.dailyWorkloadMinutes,
        horasContratuais: effectiveHorasContratuais,
        horasEscaladas: Math.round((minutes / 60) * 10) / 10,
        folgasMesAtual,
        folgasMesPrevistas,
        domingosFolgaMesAtual,
        domingosFolgaMesPrevistos,
        shifts: cells,
      };
    });
}

const toMinutes = (t: string): number | null => {
  const m = /^(\d{1,2}):(\d{2})$/.exec(t ?? '');
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};

/** Body of PUT weekly-schedules/shift/{id} for an edited grid cell. */
export function toShiftUpdate(
  cell: ShiftCell,
  scheduleId: number,
  employeeId: number,
  justification: string
): Partial<ApiScheduleShift> {
  const off = cell.tipo === 'FOLGA';
  const absent = cell.tipo === 'FERIAS' || cell.tipo === 'AFASTADO';
  const start = toMinutes(cell.entrada);
  const end = toMinutes(cell.saida);
  const bStart = toMinutes(cell.saidaIntervalo);
  const bEnd = toMinutes(cell.retornoIntervalo);
  let worked = 0;
  if (!off && !absent && start !== null && end !== null) {
    const span = end >= start ? end - start : 1440 - start + end;
    const pause = bStart !== null && bEnd !== null ? (bEnd >= bStart ? bEnd - bStart : 1440 - bStart + bEnd) : 0;
    worked = Math.max(0, span - pause);
  }
  const baseTag = off ? 'OFF' : (TYPE_TO_TAG[cell.tipo] ?? 'MID');
  const rawDom = cell.domingoFolgaMes as number | boolean | undefined;
  const shiftTag =
    rawDom !== undefined && rawDom !== null
      ? rawDom === false || rawDom === 0
        ? `${baseTag}:DOM_SEM_FOLGA_MES`
        : typeof rawDom === 'number'
          ? `${baseTag}:DOM${rawDom}`
          : `${baseTag}:DOM1`
      : off
        ? 'OFF'
        : (TYPE_TO_TAG[cell.tipo] ?? null);
  return {
    id: cell.shiftId,
    weeklyScheduleId: scheduleId,
    employeeId,
    dayOfWeekIndex: cell.dayIndex,
    isDayOff: off,
    isAbsence: absent,
    absenceType: absent ? (cell.tipo === 'FERIAS' ? 'VACATION' : 'MEDICAL_LEAVE') : null,
    startTime: off || absent ? null : cell.entrada,
    breakStartTime: off || absent || bStart === null ? null : cell.saidaIntervalo,
    breakEndTime: off || absent || bEnd === null ? null : cell.retornoIntervalo,
    endTime: off || absent ? null : cell.saida,
    workedMinutes: worked,
    shiftTag,
    justification,
    swapDayOffIndex:
      cell.permutarFolgaDiaIndex !== undefined && cell.permutarFolgaDiaIndex !== null
        ? cell.permutarFolgaDiaIndex
        : null,
    updatedAt: cell.updatedAt,
  };
}

/** Cells whose content changed between two versions of the grid (used to save only what was edited). */
export function changedCells(
  before: EmployeeWeeklySchedule[],
  after: EmployeeWeeklySchedule[]
): { employeeId: string; cell: ShiftCell }[] {
  const previous = new Map(before.map((row) => [row.employeeId, row]));
  const changes: { employeeId: string; cell: ShiftCell }[] = [];
  for (const row of after) {
    const old = previous.get(row.employeeId);
    const swapTargetIndices = new Set(
      row.shifts
        .map((s) => s.permutarFolgaDiaIndex)
        .filter((idx): idx is number => idx !== undefined && idx !== null)
    );
    for (const cell of row.shifts) {
      if (swapTargetIndices.has(cell.dayIndex)) continue;
      const oldCell = old?.shifts.find((c) => c.dayIndex === cell.dayIndex);
      const same =
        oldCell &&
        oldCell.tipo === cell.tipo &&
        oldCell.entrada === cell.entrada &&
        oldCell.saidaIntervalo === cell.saidaIntervalo &&
        oldCell.retornoIntervalo === cell.retornoIntervalo &&
        oldCell.saida === cell.saida &&
        oldCell.domingoFolgaMes === cell.domingoFolgaMes;
      if (!same && cell.shiftId) {
        changes.push({ employeeId: row.employeeId, cell });
      }
    }
  }
  return changes;
}
